import { transport } from "@/src/config/apiConfig.js";
import { createTransport } from "@/src/config/transportFactory.js";

const { transport: tasksTransport } = createTransport("tasks");

export default {
  tasks: {
    // ==================== Projects CRUD ====================
    async fetchProjects(params = {}) {
      try {
        let res = await tasksTransport.get(`/projects`, { params });
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching projects: ", error);
        throw error;
      }
    },

    async fetchProjectByID(id) {
      try {
        let res = await tasksTransport.get(`/projects/${id}`);
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching project by ID: ",
          error,
        );
        throw error;
      }
    },

    async createProject(payload) {
      try {
        let res = await tasksTransport.post(`/projects`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating project: ", error);
        throw error;
      }
    },

    async updateProject(id, payload) {
      try {
        let res = await tasksTransport.put(`/projects/${id}`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating project: ", error);
        throw error;
      }
    },

    async deleteProject(id) {
      try {
        let res = await tasksTransport.delete(`/projects/${id}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while deleting project: ", error);
        throw error;
      }
    },

    // ==================== Task Schema Management ====================
    async fetchTaskSchema(projectId) {
      try {
        let res = await tasksTransport.get(
          `/projects/${projectId}/tasks/schema`,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching task schema: ", error);
        throw error;
      }
    },

    async addTaskSchemaAttribute(projectId, payload) {
      try {
        let res = await tasksTransport.post(
          `/projects/${projectId}/tasks/attributes`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while adding task schema attribute: ",
          error,
        );
        throw error;
      }
    },

    async editTaskSchemaAttribute(projectId, attributeSlug, payload) {
      try {
        let res = await tasksTransport.put(
          `/projects/${projectId}/tasks/attributes/${attributeSlug}`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while editing task schema attribute: ",
          error,
        );
        throw error;
      }
    },

    async deleteTaskSchemaAttribute(projectId, attributeSlug) {
      try {
        let res = await tasksTransport.delete(
          `/projects/${projectId}/tasks/attributes/${attributeSlug}`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while deleting task schema attribute: ",
          error,
        );
        throw error;
      }
    },

    // ==================== Task Attribute Options ====================
    async addTaskAttributeOptions(projectId, attributeSlug, payload) {
      try {
        let res = await tasksTransport.put(
          `/projects/${projectId}/tasks/attributes/${attributeSlug}/options/bulk`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while adding task attribute options: ",
          error,
        );
        throw error;
      }
    },

    async getTaskAttributeOptions(projectId, attributeSlug) {
      try {
        let res = await tasksTransport.get(
          `/projects/${projectId}/tasks/attributes/${attributeSlug}/options`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching task attribute options: ",
          error,
        );
        throw error;
      }
    },

    // ==================== Task Option Management ====================
    async deleteTaskAttributeOption(projectId, optionId) {
      try {
        let res = await tasksTransport.delete(
          `/projects/${projectId}/tasks/options/${optionId}`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while deleting task attribute option: ",
          error,
        );
        throw error;
      }
    },

    async updateTaskAttributeOption(projectId, optionId, payload) {
      try {
        let res = await tasksTransport.put(
          `/projects/${projectId}/tasks/options/${optionId}`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while updating task attribute option: ",
          error,
        );
        throw error;
      }
    },

    // ==================== Task CRUD Operations ====================
    async createTask(projectId, payload) {
      try {
        const body = { ...payload, project_id: parseInt(projectId, 10) };
        let res = await tasksTransport.post(
          `/projects/${projectId}/tasks`,
          body,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating task: ", error);
        throw error;
      }
    },

    async listTasks(projectId, params = {}, config = {}) {
      try {
        let res = await tasksTransport.get(`/projects/${projectId}/tasks`, {
          params: params,
          ...config,
        });
        return res.data;
      } catch (error) {
        console.error("An error occurred while listing tasks: ", error);
        throw error;
      }
    },

    async getTask(projectId, taskId) {
      try {
        let res = await tasksTransport.get(
          `/projects/${projectId}/tasks/${taskId}`,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while getting task: ", error);
        throw error;
      }
    },

    async updateTask(projectId, taskId, payload) {
      try {
        let res = await tasksTransport.put(
          `/projects/${projectId}/tasks/${taskId}`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating task: ", error);
        throw error;
      }
    },

    async deleteTask(projectId, taskId) {
      try {
        let res = await tasksTransport.delete(
          `/projects/${projectId}/tasks/${taskId}`,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while deleting task: ", error);
        throw error;
      }
    },

    // ==================== Task Relationships ====================
    async createTaskRelationship(projectId, taskId, payload) {
      try {
        let res = await tasksTransport.post(
          `/projects/${projectId}/tasks/${taskId}/relationships`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while creating task relationship: ",
          error,
        );
        throw error;
      }
    },

    async bulkCreateTaskRelationships(projectId, payload) {
      try {
        let res = await tasksTransport.post(
          `/projects/${projectId}/tasks/relationships/bulk`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while bulk creating task relationships: ",
          error,
        );
        throw error;
      }
    },

    async listTaskRelationships(projectId, taskId) {
      try {
        let res = await tasksTransport.get(
          `/projects/${projectId}/tasks/${taskId}/relationships`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while listing task relationships: ",
          error,
        );
        throw error;
      }
    },

    async deleteTaskRelationship(projectId, taskId, relationshipId) {
      try {
        let res = await tasksTransport.delete(
          `/projects/${projectId}/tasks/${taskId}/relationships/${relationshipId}`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while deleting task relationship: ",
          error,
        );
        throw error;
      }
    },

    // ==================== Notes (stays on Rails - requires session auth) ====================
    async createNote(id, payload) {
      try {
        let res = await transport.post(
          `/api/projects/tasks/${id}/notes`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating note: ", error);
        throw error;
      }
    },

    async updateNote(id, payload) {
      try {
        let res = await transport.put(
          `/api/projects/tasks/notes/${id}`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating note: ", error);
        throw error;
      }
    },

    async deleteNote(id) {
      try {
        let res = await transport.delete(`/api/projects/tasks/notes/${id}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while deleting note: ", error);
        throw error;
      }
    },

    // ==================== Activities (stays on Rails - requires session auth) ====================
    async getTaskActivities(projectId, taskId, params = {}) {
      try {
        let res = await transport.get(
          `/api/projects/${projectId}/tasks/${taskId}/activities`,
          {
            params: params,
          },
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching task activities: ",
          error,
        );
        throw error;
      }
    },

    async createTaskActivity(taskId, engagementId, engagementType) {
      try {
        let res = await transport.post(
          `/api/projects/tasks/${taskId}/activities`,
          {
            engagement_id: engagementId,
            engagement_type: engagementType,
          },
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating task activity: ", error);
        throw error;
      }
    },

    // ==================== CSV Import/Export (stays on Rails) ====================
    async importCSV(payload) {
      try {
        let res = await transport.post(`/api/projects/csvbox/import`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while importing CSV: ", error);
        throw error;
      }
    },

    async fetchCSVSchema() {
      try {
        let res = await transport.get(`/api/projects/csvbox/schema`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching CSV schema: ", error);
        throw error;
      }
    },

    async exportCSV(payload) {
      try {
        let res = await transport.post(`/api/projects/csvbox/export`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while exporting CSV: ", error);
        throw error;
      }
    },

    async downloadCSVExport(exportId) {
      try {
        let res = await transport.get(
          `/api/projects/csvbox/export/${exportId}/download`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while downloading CSV export: ",
          error,
        );
        throw error;
      }
    },

    // ==================== Views (stays on Rails) ====================
    async fetchTaskViewResults(projectId, viewId, params = {}) {
      try {
        let res = await transport.get(
          `/api/projects/${projectId}/tasks/views/${viewId}/results`,
          {
            params: params,
          },
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching task view results: ",
          error,
        );
        throw error;
      }
    },

    async fetchKanbanResults(
      projectId,
      viewId,
      { page = 1, pageSize = 10, option = null, searchQuery = "" } = {},
      config = {},
    ) {
      try {
        const params = new URLSearchParams({
          page_size: pageSize,
          page: page,
        });

        if (option) {
          params.append("option", option);
        }

        if (searchQuery) {
          params.append("search_query", searchQuery);
        }

        let res = await transport.get(
          `/api/projects/${projectId}/tasks/views/${viewId}/results/kanban?${params}`,
          config,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching kanban results: ",
          error,
        );
        throw error;
      }
    },

    async fetchDynamicEntityViews() {
      try {
        let res = await transport.get(`/api/projects/v2/views`);
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching dynamic entity views: ",
          error,
        );
        throw error;
      }
    },

    // ==================== Reindex ====================
    async reindexAllTasks() {
      try {
        let res = await tasksTransport.post(`/projects/tasks/reindex-all`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while reindexing all tasks: ", error);
        throw error;
      }
    },
  },

  notes: {
    async fetchNotes({ entity_id, entity_type, page = 1, page_size = 20 } = {}) {
      try {
        const queryParams = new URLSearchParams({ page, page_size });
        if (entity_id) queryParams.append("entity_id", entity_id);
        if (entity_type) queryParams.append("entity_type", entity_type);
        let res = await transport.get(`/api/spaces/notes?${queryParams.toString()}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching notes: ", error);
        throw error;
      }
    },

    async fetchNoteByID(id, options = {}) {
      try {
        let res = await transport(`/api/spaces/notes/${id}`, { signal: options.signal });
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching note by ID: ", error);
        throw error;
      }
    },

    async createNote(payload) {
      try {
        let res = await transport.post(`/api/spaces/notes`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating note: ", error);
        throw error;
      }
    },

    async updateNote(id, payload) {
      try {
        let res = await transport.put(`/api/spaces/notes/${id}`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating note: ", error);
        throw error;
      }
    },

    async deleteNote(id) {
      try {
        let res = await transport.delete(`/api/spaces/notes/${id}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while deleting note: ", error);
        throw error;
      }
    },

    async publishNote(id, payload = {}) {
      try {
        let res = await transport.post(`/api/spaces/notes/${id}/publish`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while publishing note: ", error);
        throw error;
      }
    },

    async unpublishNote(id) {
      try {
        let res = await transport.post(`/api/spaces/notes/${id}/unpublish`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while unpublishing note: ", error);
        throw error;
      }
    },

    async updatePublishSlug(id, payload) {
      try {
        let res = await transport.patch(`/api/spaces/notes/${id}/publish-slug`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating publish slug: ", error);
        throw error;
      }
    },

    async getNoteAccessConfig(noteId) {
      try {
        let res = await transport(`/api/spaces/notes/${noteId}/access-config`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching note access config: ", error);
        throw error;
      }
    },

    async updateNoteAccessConfig(noteId, { workspaceAccess, linkAccess }) {
      try {
        let res = await transport.patch(`/api/spaces/notes/${noteId}/access-config`, {
          workspace_access: workspaceAccess,
          link_access: linkAccess,
        });
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating note access config: ", error);
        throw error;
      }
    },

    async listNoteMembers(noteId) {
      try {
        let res = await transport(`/api/spaces/notes/${noteId}/members`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while listing note members: ", error);
        throw error;
      }
    },

    async addNoteMember(noteId, { email, permission }) {
      try {
        let res = await transport.post(`/api/spaces/notes/${noteId}/members`, {
          email,
          permission,
        });
        return res.data;
      } catch (error) {
        console.error("An error occurred while adding note member: ", error);
        throw error;
      }
    },

    async updateNoteMemberPermission(noteId, email, permission) {
      try {
        let res = await transport.patch(`/api/spaces/notes/${noteId}/members/${encodeURIComponent(email)}`, {
          permission,
        });
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating note member permission: ", error);
        throw error;
      }
    },

    async removeNoteMember(noteId, email) {
      try {
        let res = await transport.delete(`/api/spaces/notes/${noteId}/members/${encodeURIComponent(email)}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while removing note member: ", error);
        throw error;
      }
    },
  },
};
